import React, { useState, useEffect } from 'react';
import "../../css/teacher/ViewReports.css";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../components/Loader";

function ViewReports() {
    const [reports, setReports] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState({
        reportType: '',
        urgency: '',
        department: '',
        status: '',
        search: ''
    });
    const [selectedReport, setSelectedReport] = useState(null);

    const reportTypes = [
        'Student Behavior',
        'Academic Concern',
        'Facility Issue',
        'Resource Request',
        'Event Proposal',
        'Technical Problem',
        'Other'
    ];

    const departments = [
        'Mathematics',
        'Science',
        'English',
        'History',
        'Computer Science',
        'Physical Education',
        'Administration'
    ];

    useEffect(() => {
        fetchReports();
    }, []);

    const fetchReports = async () => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Unauthorized! Please log in again.");
                return;
            }

            const response = await fetch('https://schoolpro-api.uniums.com/api/teacher/report/my-reports', {
                method: 'GET',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error("Failed to fetch reports");
            }

            const data = await response.json();
            setReports(data.reports || []);
        } catch (error) {
            console.error("Error fetching reports:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({
            ...filters,
            [name]: value
        });
    };

    const filteredReports = reports.filter(report => {
        return (
            (filters.reportType === '' || report.reportType === filters.reportType) &&
            (filters.urgency === '' || report.urgency === filters.urgency) &&
            (filters.department === '' || report.department === filters.department) &&
            (filters.status === '' || report.status === filters.status) &&
            (filters.search === '' ||
                report.reportTitle.toLowerCase().includes(filters.search.toLowerCase()) ||
                report.description.toLowerCase().includes(filters.search.toLowerCase()))
        );
    });

    const getUrgencyClass = (urgency) => {
        switch (urgency) {
            case 'low':
                return 'viewReport-urgency-low';
            case 'medium':
                return 'viewReport-urgency-medium';
            case 'high':
                return 'viewReport-urgency-high';
            default:
                return '';
        }
    };

    const handleViewDetails = (report) => {
        setSelectedReport(report);
    };

    const closeModal = () => {
        setSelectedReport(null);
    };

    const formatDate = (dateString) => {
        const options = { year: 'numeric', month: 'short', day: 'numeric' };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    const handleSubmitDraft = async (reportId) => {
        setIsLoading(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) {
                toast.error("Unauthorized! Please log in again.");
                return;
            }

            const response = await fetch(`https://schoolpro-api.uniums.com/api/teacher/report/submit-draft/${reportId}`, {
                method: 'PUT',
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                throw new Error("Failed to submit draft");
            }

            const data = await response.json();
            toast.success(data.message);

            // Update the report in the state
            setReports(reports.map(report =>
                report._id === reportId ? { ...report, status: 'submitted' } : report
            ));

            // Close the modal and refresh reports
            closeModal();
            fetchReports();
        } catch (error) {
            console.error("Error submitting draft:", error);
            toast.error(`Error: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="viewReport-container">
            <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} />
            {isLoading && <Loader />}

            <div className="viewReport-header">
                <h1>My Reports</h1>
                <p>View and manage all your submitted reports</p>
            </div>

            <div className="viewReport-dashboard">
                <div className="viewReport-filters-container">
                    <div className="viewReport-search-container">
                        <i className="viewReport-search-icon">🔍</i>
                        <input
                            type="text"
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                            placeholder="Search reports..."
                            className="viewReport-search-input"
                        />
                    </div>

                    <div className="viewReport-filter-controls">
                        <div className="viewReport-filter-group">
                            <label>Report Type</label>
                            <select
                                name="reportType"
                                value={filters.reportType}
                                onChange={handleFilterChange}
                                className="viewReport-select"
                            >
                                <option value="">All Types</option>
                                {reportTypes.map((type, index) => (
                                    <option key={index} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>

                        <div className="viewReport-filter-group">
                            <label>Urgency</label>
                            <select
                                name="urgency"
                                value={filters.urgency}
                                onChange={handleFilterChange}
                                className="viewReport-select"
                            >
                                <option value="">All Levels</option>
                                <option value="low">Low</option>
                                <option value="medium">Medium</option>
                                <option value="high">High</option>
                            </select>
                        </div>

                        <div className="viewReport-filter-group">
                            <label>Department</label>
                            <select
                                name="department"
                                value={filters.department}
                                onChange={handleFilterChange}
                                className="viewReport-select"
                            >
                                <option value="">All Departments</option>
                                {departments.map((dept, index) => (
                                    <option key={index} value={dept}>{dept}</option>
                                ))}
                            </select>
                        </div>
                        <div className="viewReport-filter-group">
                            <label>Status</label>
                            <select
                                name="status"
                                value={filters.status}
                                onChange={handleFilterChange}
                                className="viewReport-select"
                            >
                                <option value="">All Statuses</option>
                                <option value="draft">Drafts</option>
                                <option value="submitted">Submitted</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="viewReport-results-summary">
                    <h2>Results: <span>{filteredReports.length} Reports</span></h2>
                    <button className="viewReport-refresh-button" onClick={fetchReports}>
                        Refresh Reports
                    </button>
                </div>

                <div className="viewReport-reports-grid">
                    {filteredReports.length === 0 ? (
                        <div className="viewReport-no-reports">
                            <div className="viewReport-empty-icon">📋</div>
                            <h3>No Reports Found</h3>
                            <p>No reports match your current filter criteria.</p>
                            <button
                                className="viewReport-button viewReport-button-secondary"
                                onClick={() => setFilters({
                                    reportType: '',
                                    urgency: '',
                                    department: '',
                                    search: ''
                                })}
                            >
                                Clear Filters
                            </button>
                        </div>
                    ) : (
                        filteredReports.map((report) => (
                            <div key={report._id} className="viewReport-report-card">
                                <div className="viewReport-card-header">
                                    <div className="viewReport-card-header-content">
                                        <span className={`viewReport-urgency-badge ${getUrgencyClass(report.urgency)}`}>
                                            {report.urgency.charAt(0).toUpperCase() + report.urgency.slice(1)}
                                        </span>
                                        {report.status === 'draft' && (
                                            <span className="viewReport-draft-badge">Draft</span>
                                        )}
                                        <span className="viewReport-report-date">{formatDate(report.date)}</span>
                                    </div>
                                    <h3>{report.reportTitle}</h3>
                                </div>
                                <div className="viewReport-card-body">
                                    <div className="viewReport-card-details">
                                        <div className="viewReport-detail-item">
                                            <span className="viewReport-detail-label">Type:</span>
                                            <span className="viewReport-report-type">{report.reportType}</span>
                                        </div>
                                        <div className="viewReport-detail-item">
                                            <span className="viewReport-detail-label">Department:</span>
                                            <span className="viewReport-department">{report.department || 'Not specified'}</span>
                                        </div>
                                    </div>
                                    <p className="viewReport-description">{report.description}</p>
                                    {report.attachments?.length > 0 && (
                                        <div className="viewReport-attachments">
                                            <span className="viewReport-attachment-icon">📎</span>
                                            <span>{report.attachments.length} attachment{report.attachments.length !== 1 ? 's' : ''}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="viewReport-card-footer">
                                    <button
                                        className="viewReport-button viewReport-button-primary"
                                        onClick={() => handleViewDetails(report)}
                                    >
                                        View Details
                                    </button>

                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>

            {selectedReport && (
                <div className="viewReport-modal-overlay" onClick={closeModal}>
                    <div className="viewReport-modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="viewReport-modal-header">
                            <h2>{selectedReport.reportTitle}</h2>
                            <button className="viewReport-modal-close" onClick={closeModal}>×</button>
                        </div>
                        <div className="viewReport-modal-body">
                            <div className="viewReport-modal-details">
                                <div className="viewReport-modal-detail-row">
                                    <div className="viewReport-modal-detail-item">
                                        <span className="viewReport-modal-detail-label">Report Type</span>
                                        <span className="viewReport-modal-detail-value">{selectedReport.reportType}</span>
                                    </div>
                                    <div className="viewReport-modal-detail-item">
                                        <span className="viewReport-modal-detail-label">Department</span>
                                        <span className="viewReport-modal-detail-value">{selectedReport.department || 'Not specified'}</span>
                                    </div>
                                </div>
                                <div className="viewReport-modal-detail-row">
                                    <div className="viewReport-modal-detail-item">
                                        <span className="viewReport-modal-detail-label">Urgency</span>
                                        <span className={`viewReport-modal-urgency ${getUrgencyClass(selectedReport.urgency)}`}>
                                            {selectedReport.urgency.charAt(0).toUpperCase() + selectedReport.urgency.slice(1)}
                                        </span>
                                    </div>
                                    <div className="viewReport-modal-detail-item">
                                        <span className="viewReport-modal-detail-label">Submitted On</span>
                                        <span className="viewReport-modal-detail-value">{formatDate(selectedReport.date)}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="viewReport-modal-description-container">
                                <h3>Description</h3>
                                <p className="viewReport-modal-description">{selectedReport.description}</p>
                            </div>
                            {selectedReport.attachments?.length > 0 && (
                                <div className="viewReport-modal-attachments">
                                    <h3>Attachments ({selectedReport.attachments.length})</h3>
                                    <div className="viewReport-modal-attachment-list">
                                        {selectedReport.attachments.map((attachment, index) => (
                                            <div key={index} className="viewReport-modal-attachment-item">
                                                <span className="viewReport-attachment-icon">📎</span>
                                                <span>Attachment {index + 1}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className="viewReport-modal-footer">
                            <button className="viewReport-button viewReport-button-secondary" onClick={closeModal}>Close</button>

                            {selectedReport.status === 'draft' && (
                                <>
                                    <button
                                        className="viewReport-button viewReport-button-primary"
                                        onClick={() => handleSubmitDraft(selectedReport._id)}
                                    >
                                        Submit Report
                                    </button>

                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default ViewReports;